/* eslint-disable sort/object-properties */
import {
    ALPHA_2_ISO_3166,
    ALPHA_2_ISO_3166_EU,
    APPLICANT_ATTRIBUTION_BOTH,
    LIMITED_RESIDENCE_PERMIT_TYPES,
} from 'cpool-constants';
import { Personendaten } from 'cpool-schemas';
import { z } from 'zod';

import { checkIBAN } from '~/features/leadDetails/utils/checkIBAN.ts';

const schemaAntragssteller = z.object({
    personendaten: Personendaten.omit({ familienstand: true })
        .extend({
            aufenthaltBefristetBis: Personendaten.shape.aufenthaltBefristetBis.refine(
                (date) => {
                    if (!date) return true;

                    const currentDate = new Date();
                    const oneDayAhead = new Date(
                        currentDate.getFullYear(),
                        currentDate.getMonth(),
                        currentDate.getDate(),
                    );
                    oneDayAhead.setDate(oneDayAhead.getDate() + 1);
                    const trimmedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                    return trimmedDate >= oneDayAhead;
                },
                {
                    params: { i18n: 'errors.dateMustBeInFuture' },
                },
            ),
            inDeutschlandSeit: Personendaten.shape.inDeutschlandSeit.refine(
                (date) => {
                    if (!date) return true;

                    const currentDate = new Date();
                    const oneDayBefore = new Date(
                        currentDate.getFullYear(),
                        currentDate.getMonth(),
                        currentDate.getDate(),
                    );
                    oneDayBefore.setDate(oneDayBefore.getDate() - 1);
                    const trimmedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                    return trimmedDate <= oneDayBefore;
                },
                {
                    params: { i18n: 'errors.dateMustBeInPast' },
                },
            ),
        })
        .refine(
            (personendaten) => {
                if (
                    !(Object.values(ALPHA_2_ISO_3166_EU) as string[]).includes(personendaten.staatsangehoerigkeit) &&
                    !personendaten.aufenthaltstitel
                )
                    return false;
                return true;
            },
            {
                params: { i18n: 'errors.aufenthaltstitelRequired' },
            },
        )
        .refine(
            (personendaten) => {
                if (
                    personendaten.aufenthaltstitel &&
                    (LIMITED_RESIDENCE_PERMIT_TYPES as unknown as string[]).includes(personendaten.aufenthaltstitel) &&
                    !personendaten.aufenthaltBefristetBis
                )
                    return false;
                return true;
            },
            {
                params: { i18n: 'errors.residencePermitUntilRequired' },
            },
        )
        .refine(
            (personendaten) => {
                if (personendaten.staatsangehoerigkeit !== ALPHA_2_ISO_3166.DE && !personendaten.inDeutschlandSeit)
                    return false;
                return true;
            },
            {
                params: { i18n: 'errors.inGermanySinceRequired' },
            },
        ),
});
export type SchemaPersonalApplicant = z.infer<typeof schemaAntragssteller>;

const defaultKontoverbindung = {
    gehoertZuAntragsteller: APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1,
    iban: null,
    bic: null,
    kreditinstitut: null,
} as const;

const haushaltSchema = z.preprocess(
    (data: unknown) => {
        if (data == null || typeof data !== 'object' || !('kontoverbindung' in data)) {
            return { kontoverbindung: { ...defaultKontoverbindung } };
        }
        const h = data as { kontoverbindung?: unknown };
        if (h.kontoverbindung == null) {
            return { ...h, kontoverbindung: { ...defaultKontoverbindung } };
        }
        const k = h.kontoverbindung as Record<string, unknown>;
        return {
            ...h,
            kontoverbindung: {
                gehoertZuAntragsteller: k.gehoertZuAntragsteller ?? APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1,
                iban: k.iban === undefined || k.iban === '' ? null : k.iban,
                bic: k.bic === undefined || k.bic === '' ? null : k.bic,
                kreditinstitut: k.kreditinstitut === undefined || k.kreditinstitut === '' ? null : k.kreditinstitut,
            },
        };
    },
    z.object({
        kontoverbindung: z.object({
            gehoertZuAntragsteller: z.nativeEnum(APPLICANT_ATTRIBUTION_BOTH),
            iban: z.union([z.null(), z.string()]).refine((iban) => iban == null || checkIBAN(iban)),
            bic: z.union([z.null(), z.string().regex(/^([A-Z]{6}[A-Z2-9][A-NP-Z1-9])(X{3}|[A-WY-Z0-9][A-Z0-9]{2})?$/)]),
            kreditinstitut: z.union([z.null(), z.string().min(1)]),
        }),
    }),
);

export const schemaPersonal = z.object({
    antragsteller1: schemaAntragssteller,
    antragsteller2: schemaAntragssteller.nullable().default(null),
    haushalt: haushaltSchema,
});
export type SchemaPersonal = z.infer<typeof schemaPersonal>;
